import React, { useState } from "react";
import { X, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton } from "@/components/kit";
import { inputClass } from "@/components/AuthShell";

export default function VerifyEmailModal({ open, onClose }) {
  const { user, verifyEmail, resendVerification } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error("Enter the 6-digit code.");
    setLoading(true);
    try {
      await verifyEmail(code.trim());
      toast.success("Email verified! You're all set. 🎉");
      onClose();
    } catch (err) {
      toast.error(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await resendVerification();
      toast.success("A fresh code is on its way to your inbox.");
    } catch (err) {
      toast.error(err.message || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="verify-email-modal">
        <div className="flex justify-end">
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F2EFE9] flex items-center justify-center" data-testid="verify-close">
            <X size={18} />
          </button>
        </div>
        <div className="text-center -mt-2">
          <div className="h-16 w-16 rounded-3xl bg-[#F6DED5] flex items-center justify-center mx-auto mb-3">
            <MailCheck size={30} color="#E86A45" />
          </div>
          <h2 className="font-display text-2xl font-extrabold">Verify your email</h2>
          <p className="text-[#4A4746] mt-1 text-sm">
            We sent a 6-digit code to <b className="text-[#2D2B2A]">{user?.email}</b>. Enter it below to unlock AI health chat.
          </p>
        </div>
        <form onSubmit={submit} className="mt-5">
          <input
            data-testid="verify-code-input"
            inputMode="numeric"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            className={inputClass + " tracking-[0.4em] text-center text-xl font-bold"}
          />
          <PrimaryButton data-testid="verify-submit-btn" type="submit" loading={loading} className="w-full mt-4">
            Verify email
          </PrimaryButton>
        </form>
        <button
          data-testid="verify-resend-btn"
          onClick={resend}
          disabled={resending}
          className="w-full mt-3 text-sm font-bold text-[#6F8F72] disabled:opacity-60"
        >
          {resending ? "Sending…" : "Didn't get it? Resend code"}
        </button>
      </div>
    </div>
  );
}
